/**
 * Retrieves a specific extra information item from a CMS based on a delivery key.
 *
 * @param {string} deliveryKey - A unique identifier for fetching a specific extra information item.
 * @returns {Promise<UseFetchReturn>} A fetch result with the retrieved extra information item.
 *
 * @description
 * This function fetches a single content item or metadata from a CMS:
 * - Supports localized content retrieval
 * - Allows optional query parameters for content variations
 * - Uses a lazy-loaded fetch mechanism for flexible content loading
 *
 * @example
 * // Fetch a specific extra information item
 * const { data, execute } = await useExtraInfoItem('featured-product')
 *
 * @remarks
 * - Requires an active route and localization setup
 * - Supports dynamic, lazy-loaded content retrieval
 * - Provides more granular content fetching compared to useExtraInfo
 * - The fetch is not immediate, allowing manual execution
 */
export async function useExtraInfoItem(deliveryKey: string) {
  const localePath = useLocalePath()

  const query = {
    locale: localePath(''),
    vse: useRoute().query.vse,
    contentId: useRoute().query.contentId,
    deliveryKey: deliveryKey,
  }

  return useFetch('/api/cms/getExtraInfoItem', {
    query,
    immediate: false,
  })
}
